import { createContext, useContext, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { formatList, formatNumber } from '../../lib/format'
import type { ReadDocument, ReadField, ReadingFlag, ReadStop } from './api'
import { dayText, flagText, money, type FlagsByField } from './describe'

/** The currency the document printed: its flags' amounts are said in it, as its facts are. */
const DocumentCurrency = createContext<string | null>(null)

/**
 * One rate confirmation as it was read (3.1): every fact with the page it was read on and the
 * words that show it, or why it is not there — not on the document, or there and illegible.
 * The platform's flags sit under the field they are about. Read-only: correcting a value is
 * the review's (3.2).
 */
export function DocumentReading({ document, flags }: { document: ReadDocument; flags: FlagsByField }) {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const currency = document.money.currency.value
  const text = (value: string) => value
  const count = (value: number) => formatNumber(value, language)
  const amount = (value: number) => money(value, currency, language)
  const day = (value: string) => dayText(value, language)
  const label = (key: string) => t(`rateConfirmations.fields.${key}`)

  return (
    <DocumentCurrency.Provider value={currency}>
      <Section title={t('rateConfirmations.sections.broker')}>
        <Fact label={label('brokerName')} field={document.broker.name} show={text} flags={flags.get('broker.name')} />
        <Fact label={label('mcNumber')} field={document.broker.mcNumber} show={text} flags={flags.get('broker.mc_number')} />
        <Fact label={label('usdotNumber')} field={document.broker.usdotNumber} show={text} flags={flags.get('broker.usdot_number')} />
        <Fact label={label('address')} field={document.broker.address} show={text} />
        <Fact label={label('agentName')} field={document.broker.agentName} show={text} />
        <Fact label={label('agentEmail')} field={document.broker.agentEmail} show={text} />
        <Fact label={label('agentPhone')} field={document.broker.agentPhone} show={text} />
      </Section>

      <Section title={t('rateConfirmations.sections.carrier')}>
        <Fact label={label('carrierName')} field={document.carrier.name} show={text} flags={flags.get('carrier.name')} />
        <Fact label={label('mcNumber')} field={document.carrier.mcNumber} show={text} flags={flags.get('carrier.mc_number')} />
        <Fact label={label('usdotNumber')} field={document.carrier.usdotNumber} show={text} flags={flags.get('carrier.usdot_number')} />
      </Section>

      <Section title={t('rateConfirmations.sections.load')}>
        <Fact label={label('loadNumber')} field={document.loadNumber} show={text} />
        <Fact label={label('issuedOn')} field={document.issuedOn} show={day} />
      </Section>

      <Section title={t('rateConfirmations.sections.equipment')}>
        <Fact label={label('asPrinted')} field={document.equipment.asPrinted} show={text} />
        <Listed
          label={label('equipmentTypes')}
          items={document.equipment.types.map((type) => t(`rateConfirmations.equipmentTypes.${type}`))}
          flags={flags.get('equipment.types')}
        />
        <Listed
          label={label('features')}
          items={document.equipment.features.map((feature) => t(`rateConfirmations.features.${feature}`))}
        />
        <Fact
          label={label('lengthFeet')}
          field={document.equipment.lengthFeet}
          show={(value) => t('rateConfirmations.feet', { count: value })}
        />
      </Section>

      <Section title={t('rateConfirmations.sections.freight')}>
        <Fact
          label={label('loadType')}
          field={document.freight.loadType}
          show={(value) => t(`rateConfirmations.loadTypes.${value}`)}
        />
        <Fact
          label={label('weight')}
          field={document.freight.weightPounds}
          show={(value) => t('rateConfirmations.pounds', { weight: count(value) })}
          flags={flags.get('freight.weight_pounds')}
        />
        <Fact label={label('pieces')} field={document.freight.pieces} show={count} />
        <Fact label={label('pallets')} field={document.freight.pallets} show={count} />
        <Fact label={label('commodity')} field={document.freight.commodity} show={text} />
        <Fact label={label('miles')} field={document.freight.miles} show={count} />
        <Fact label={label('temperature')} field={document.freight.temperature} show={text} />
        <Fact
          label={label('hazmat')}
          field={document.freight.hazmat}
          show={(value) => t(value ? 'rateConfirmations.yes' : 'rateConfirmations.no')}
        />
      </Section>

      <Section title={t('rateConfirmations.sections.money')}>
        <Fact label={label('lineHaul')} field={document.money.lineHaul} show={amount} flags={flags.get('money.line_haul')} />
        {document.money.accessorials.map((charge, index) => (
          <Fact
            key={`charge-${index}`}
            label={t('rateConfirmations.fields.charge', {
              kind: t(`rateConfirmations.chargeKinds.${charge.kind}`),
            })}
            field={{ value: charge.amount, page: charge.page, quote: charge.quote, missing: null }}
            show={amount}
            note={charge.description}
            flags={flags.get(`money.accessorials[${index + 1}].amount`)}
          />
        ))}
        <Fact label={label('total')} field={document.money.total} show={amount} flags={flags.get('money.total')} />
        <Fact label={label('currency')} field={document.money.currency} show={text} flags={flags.get('money.currency')} />
      </Section>

      {/* The route's own heading, where a flag about the whole route — no pickup, no
          delivery — belongs; each stop follows as a section of its own. */}
      <section className="rc-section">
        <h3 className="rc-section__title">{t('rateConfirmations.sections.stops')}</h3>
        <Flags flags={flags.get('stops')} />
        {document.stops.length === 0 && <p className="muted">{t('rateConfirmations.noStops')}</p>}
      </section>
      {document.stops.map((stop, index) => (
        <Stop key={`stop-${index}`} stop={stop} position={index + 1} flags={flags} />
      ))}

      <Section title={t('rateConfirmations.sections.terms')}>
        <Fact label={label('accessorialTerms')} field={document.terms.accessorialTerms} show={text} wide />
        <Fact label={label('paymentTerms')} field={document.terms.paymentTerms} show={text} />
        <Fact label={label('invoiceTo')} field={document.terms.invoiceTo} show={text} />
        <Fact
          label={label('returnMethod')}
          field={document.terms.returnMethod}
          show={(value) => t(`rateConfirmations.returnMethods.${value}`)}
        />
        <Fact label={label('returnTo')} field={document.terms.returnTo} show={text} />
        <Listed
          label={label('signatureAsks')}
          items={document.terms.signatureAsks.map((ask) => t(`rateConfirmations.signatureAsks.${ask}`))}
        />
      </Section>
    </DocumentCurrency.Provider>
  )
}

/** One stop as written (D9): its place, its days and times as the document gives them, its numbers. */
function Stop({ stop, position, flags }: { stop: ReadStop; position: number; flags: FlagsByField }) {
  const { t, i18n } = useTranslation()
  const path = (part: string) => `stops[${position}].${part}`
  const label = (key: string) => t(`rateConfirmations.fields.${key}`)
  const text = (value: string) => value
  const day = (value: string) => dayText(value, i18n.language)

  return (
    <Section
      title={t('rateConfirmations.stopTitle', { number: position, kind: t(`rateConfirmations.stopKinds.${stop.kind}`) })}
      level="h4"
    >
      <Fact label={label('company')} field={stop.company} show={text} />
      <Fact label={label('street')} field={stop.street} show={text} />
      <Fact label={label('city')} field={stop.city} show={text} flags={flags.get(path('city'))} />
      <Fact label={label('state')} field={stop.state} show={text} flags={flags.get(path('state'))} />
      <Fact label={label('zip')} field={stop.zip} show={text} />
      <Fact label={label('date')} field={stop.date} show={day} flags={flags.get(path('date'))} />
      <Fact label={label('lastDate')} field={stop.lastDate} show={day} flags={flags.get(path('last_date'))} />
      <Fact label={label('timeFrom')} field={stop.timeFrom} show={text} flags={flags.get(path('time_from'))} />
      <Fact label={label('timeTo')} field={stop.timeTo} show={text} flags={flags.get(path('time_to'))} />
      <Fact
        label={label('appointment')}
        field={stop.appointment}
        show={(value) => t(`rateConfirmations.appointments.${value}`)}
      />
      <Listed label={label('references')} items={stop.references.map((reference) => `${reference.label} ${reference.value}`)} />
      <Fact label={label('notes')} field={stop.notes} show={text} wide />
    </Section>
  )
}

function Section({ title, children, level = 'h3' }: { title: string; children: ReactNode; level?: 'h3' | 'h4' }) {
  const Heading = level

  return (
    <section className="rc-section">
      <Heading className="rc-section__title">{title}</Heading>
      <dl className="rc-facts">{children}</dl>
    </section>
  )
}

/**
 * One fact, its evidence, and what the platform noticed about it. A value the model could not
 * read says why — "not on the document" is a fact about the paper; "illegible" asks a person
 * to look, and wears the warning's colour.
 */
function Fact<T>({
  label,
  field,
  show,
  flags,
  wide = false,
  note,
}: {
  label: string
  field: ReadField<T>
  show: (value: T) => string
  flags?: readonly ReadingFlag[]
  wide?: boolean
  /**
   * What the document says beside the value, shown whether or not the value was read — a
   * charge's description stays when its amount is the part that could not be (GPT-6.1 Sol on
   * #66).
   */
  note?: string
}) {
  const { t } = useTranslation()
  const flagged = flags !== undefined && flags.length > 0
  const className = ['rc-fact', wide ? 'rc-fact--wide' : null, flagged ? 'rc-fact--flagged' : null]
    .filter((name) => name !== null)
    .join(' ')

  return (
    <div className={className}>
      <dt>{label}</dt>
      <dd>
        {field.value !== null ? (
          <span className="rc-fact__value">{show(field.value)}</span>
        ) : field.missing === 'Illegible' ? (
          <span className="rc-fact__illegible">{t('rateConfirmations.missing.Illegible')}</span>
        ) : field.missing === 'NotOnDocument' ? (
          <span className="muted">{t('rateConfirmations.missing.NotOnDocument')}</span>
        ) : (
          // A charge listed with no amount carries no reason of its own.
          <span className="muted">{t('rateConfirmations.missing.notRead')}</span>
        )}
        {note !== undefined && note !== '' && <span className="rc-fact__note">{note}</span>}
        <Evidence page={field.page} quote={field.quote} />
        <Flags flags={flags} />
      </dd>
    </div>
  )
}

/** A list of values with no evidence of their own — the types, features and asks a document names. */
function Listed({ label, items, flags }: { label: string; items: readonly string[]; flags?: readonly ReadingFlag[] }) {
  const { t, i18n } = useTranslation()
  const flagged = flags !== undefined && flags.length > 0

  return (
    <div className={flagged ? 'rc-fact rc-fact--flagged' : 'rc-fact'}>
      <dt>{label}</dt>
      <dd>
        {items.length === 0 ? (
          <span className="muted">{t('rateConfirmations.missing.none')}</span>
        ) : (
          <span className="rc-fact__value">{formatList(items, i18n.language)}</span>
        )}
        <Flags flags={flags} />
      </dd>
    </div>
  )
}

/** The page a fact was read on and the words that show it — what a person checks against the PDF. */
function Evidence({ page, quote }: { page: number | null; quote: string | null }) {
  const { t } = useTranslation()

  if (page === null && (quote === null || quote === '')) {
    return null
  }

  return (
    <span className="rc-fact__evidence">
      {page !== null && t('rateConfirmations.evidence.page', { page })}
      {page !== null && quote !== null && quote !== '' && ' · '}
      {quote !== null && quote !== '' && <q>{quote}</q>}
    </span>
  )
}

function Flags({ flags }: { flags?: readonly ReadingFlag[] }) {
  const { t, i18n } = useTranslation()
  const currency = useContext(DocumentCurrency)

  if (flags === undefined || flags.length === 0) {
    return null
  }

  return (
    <ul className="rc-fact__flags">
      {flags.map((flag, index) => (
        <li key={`${flag.code}-${index}`}>{flagText(flag, t, i18n.language, currency)}</li>
      ))}
    </ul>
  )
}
